import { Popover, Transition } from '@headlessui/react';
import { DownArrowIcon } from 'assets/icons';
import { FC, useEffect, useState } from 'react';
import { Calendar } from 'react-date-range';
import DateUtils from 'utils/dateUtils';
import classNames from 'classnames';

interface DayPickerProps {
  className?: string;
  showPastDates?: boolean;
  onSelect: (value: Date) => void;
  date: Date;
  prefixIcon?: React.ReactNode;
  labelClassName?: string;
  disabled?: boolean;
  timeZone?: string;
  disableBeforeDate?: Date | string;
}

const DayPicker: FC<DayPickerProps> = ({
  showPastDates = false,
  onSelect,
  date = new Date(),
  prefixIcon,
  labelClassName,
  disabled = false,
  timeZone = '',
  disableBeforeDate = null
}) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(date ?? null);

  useEffect(() => {
    setSelectedDate(date);
  }, [date]);

  const handleSelect = value => {
    setSelectedDate(value);
    onSelect(value);
  };

  return (
    <Popover className="relative">
      {({ close }) => (
        <>
          <Popover.Button className="w-full" disabled={disabled}>
            <div className="flex w-full items-center justify-between rounded bg-secondaryBtn py-3.5 px-5">
              <div className={classNames('flex items-center text-primaryText', labelClassName)}>
                {prefixIcon && <div className="mr-2">{prefixIcon}</div>}
                {DateUtils.getDateInFormat({
                  date: new Date(selectedDate),
                  dateFormat: 'MMMM dd, yyyy'
                })}
                {timeZone && <span className="ml-1 text-sbase text-primaryText">({timeZone})</span>}
              </div>
              <DownArrowIcon />
            </div>
          </Popover.Button>

          <Transition
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0">
            <Popover.Panel className="absolute -top-2.5 z-50 w-full rounded-md">
              <Calendar
                date={selectedDate}
                onChange={value => {
                  handleSelect(value);
                  close();
                }}
                {...(!showPastDates && { minDate: disableBeforeDate ? new Date(disableBeforeDate) : new Date() })}
              />
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
};

export default DayPicker;
